import { Link, Navigate, useParams } from "react-router";
import { ChevronRight } from "lucide-react";
import {
  PLATFORM_TOPIC_SLUGS,
  platformTopicCopy,
  type PlatformTopicSlug,
} from "../data/platformContent";
import {
  PlatformAIIntelligenceSection,
  PlatformArchitectureFeaturesSection,
  PlatformDeploymentDiagramSection,
  PlatformEnterpriseIdentityFocusSection,
  PlatformPrivacySection,
  PlatformSecuritySection,
} from "../components/platform/PlatformSections";
import { MarketingBottomCta } from "../components/MarketingBottomCta";
import { Seo } from "../components/Seo";

function isPlatformSlug(s: string | undefined): s is PlatformTopicSlug {
  return !!s && (PLATFORM_TOPIC_SLUGS as string[]).includes(s);
}

export function PlatformTopicPage() {
  const { slug } = useParams();

  if (!isPlatformSlug(slug)) {
    return <Navigate to="/platform" replace />;
  }

  const meta = platformTopicCopy[slug];
  const cta = meta.marketingCta;

  return (
    <div className="bg-slate-950 min-h-screen">
      <Seo
        title={meta.pageTitle}
        description={meta.metaDescription}
        canonicalPath={`/platform/${slug}`}
      />

      <section className="relative pt-36 pb-12 overflow-hidden border-b border-teal-500/10">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(5, 150, 105, 0.12) 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-xs text-on-dark-muted mb-8">
            <Link to="/" className="hover:text-teal-400 transition-colors">
              Home
            </Link>
            <span className="mx-2 text-slate-500">/</span>
            <Link to="/platform" className="hover:text-teal-400 transition-colors">
              Platform
            </Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-slate-400 capitalize">{slug.replace(/-/g, " ")}</span>
          </nav>

          <h1
            className="text-slate-100 mb-4 max-w-4xl"
            style={{
              fontSize: "clamp(1.85rem, 4vw, 2.75rem)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: "1.15",
            }}
          >
            {meta.pageTitle}
          </h1>
          <p className="text-on-dark-muted text-lg max-w-3xl leading-relaxed mb-8">{meta.metaDescription}</p>
          <Link
            to="/platform"
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm transition-colors"
          >
            <ChevronRight size={16} className="rotate-180" />
            Full platform overview
          </Link>
        </div>
      </section>

      {slug === "security" && <PlatformSecuritySection />}
      {slug === "privacy" && <PlatformPrivacySection />}
      {slug === "architecture" && (
        <section className="py-16 border-t border-slate-800/50">
          <PlatformArchitectureFeaturesSection />
          <div className="mt-16">
            <PlatformDeploymentDiagramSection />
          </div>
        </section>
      )}
      {slug === "ai-intelligence" && <PlatformAIIntelligenceSection />}
      {slug === "identity" && <PlatformEnterpriseIdentityFocusSection />}
      {slug === "deployment" && (
        <section className="py-16 border-t border-slate-800/50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
            <p className="text-on-dark-muted leading-relaxed">
              Agents stream over encrypted connections into a backend you control; the investigator console runs on
              your domain with OIDC from your IdP. Everything stays inside your perimeter, ideal for regulated networks and
              phased rollouts.
            </p>
          </div>
          <PlatformDeploymentDiagramSection />
        </section>
      )}

      <MarketingBottomCta titleBefore={cta.titleBefore} titleAccent={cta.titleAccent} description={cta.description} />
    </div>
  );
}
